import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_133: DocMigration = {
  maxSchemaVersion: "0.0.133",
  description: "Roof settings refactoring",
  apply(json: any) {
    const { settings: allSettings, roofs, dormers } = json;

    const renameSettingsParameter = (roofSettings: any, srcName: string, dstName: string | string[]) => {
      if (roofSettings[srcName] !== undefined) {
        if (Array.isArray(dstName)) {
          for (const name of dstName) {
            roofSettings[name] = roofSettings[srcName];
          }
        } else {
          roofSettings[dstName] = roofSettings[srcName];
        }

        delete roofSettings[srcName];
      }
    };

    const renameRoofParameter = (roof: any, srcName: string, dstName: string | string[] = srcName) => {
      if (roof[srcName] !== undefined) {
        if (Array.isArray(dstName)) {
          for (const name of dstName) {
            roof.roofSettings[name] = roof[srcName];
          }
        } else {
          roof.roofSettings[dstName] = roof[srcName];
        }

        delete roof[srcName];
      }
    };

    const transformRoofParameter = (
      roof: any,
      srcName: string,
      dstName: string = srcName,
      transform: (param: any) => any,
    ) => {
      if (roof[srcName] !== undefined) {
        roof.roofSettings[dstName] = transform(roof[srcName]);
        delete roof[srcName];
      }
    };

    const renameDormerParameter = (dormer: any, srcName: string, dstName: string | string[]) => {
      if (dormer[srcName] !== undefined) {
        if (Array.isArray(dstName)) {
          for (const name of dstName) {
            dormer[name] = dormer[srcName];
          }
        } else {
          dormer[dstName] = dormer[srcName];
        }

        delete dormer[srcName];
      }
    };

    const transformDormerParameter = (
      dormer: any,
      srcName: string,
      dstName: string = srcName,
      transform: (param: any) => any,
    ) => {
      if (dormer[srcName] !== undefined) {
        dormer[dstName] = transform(dormer[srcName]);
        delete dormer[srcName];
      }
    };

    // roof settings
    for (const settingsId in allSettings) {
      const settings = allSettings[settingsId];
      if (settings.type !== "ROOF_SETTINGS") {
        continue;
      }

      renameSettingsParameter(settings, "overhang", ["eaveOverhang", "rakeOverhang"]);
      renameSettingsParameter(settings, "sideRoofOverhangOffset", "overhangSideOffset");
      renameSettingsParameter(settings, "includeFasciaTrim", "includeFasciaTopTrim");
      renameSettingsParameter(settings, "fasciaTrimSize", "fasciaTopTrimSize");
      renameSettingsParameter(settings, "includeRidgeVentsOnRoofPlans", "includeRidgeVent");
    }

    // roof instances
    for (const roofId in roofs) {
      const roof = roofs[roofId];

      roof.roofSettings = {};

      renameRoofParameter(roof, "peakDirection");
      renameRoofParameter(roof, "parentRoofNeighborMode", "weldMode");

      renameRoofParameter(roof, "gableRoofType", "roofType");

      renameRoofParameter(roof, "structureType");
      renameRoofParameter(roof, "thickness", "vaultedRoofThickness");
      renameRoofParameter(roof, "rightThickness", "vaultedRoofRightThickness");

      if (roof.startTruncation !== undefined) {
        roof.roofSettings.startTruncationType = roof.startTruncation.type ?? "TRUNCATED";
        roof.roofSettings.startTruncationHeightFactor = roof.startTruncation.heightFactor;
        roof.roofSettings.startTruncationNormalizedHeight = roof.startTruncation.normalizedHeight;
        roof.roofSettings.startTruncationSlopeType = roof.startTruncation.slopeType;
        roof.roofSettings.startTruncationSlope = roof.startTruncation.slope;
        delete roof.startTruncation;
      }

      if (roof.endTruncation !== undefined) {
        roof.roofSettings.endTruncationType = roof.endTruncation.type ?? "TRUNCATED";
        roof.roofSettings.endTruncationHeightFactor = roof.endTruncation.heightFactor;
        roof.roofSettings.endTruncationNormalizedHeight = roof.endTruncation.normalizedHeight;
        roof.roofSettings.endTruncationSlopeType = roof.endTruncation.slopeType;
        roof.roofSettings.endTruncationSlope = roof.endTruncation.slope;
        delete roof.endTruncation;
      }

      renameRoofParameter(roof, "overhang", ["eaveOverhang", "rakeOverhang"]);

      renameRoofParameter(roof, "sideRoofOverhangOffset", "overhangSideOffset");
      renameRoofParameter(roof, "backOverhangOffset", "overhangRearOffset");

      renameRoofParameter(roof, "slope");
      renameRoofParameter(roof, "slopes");
      renameRoofParameter(roof, "rightSlope");
      renameRoofParameter(roof, "rightSlopes");

      transformRoofParameter(roof, "clipToPerimeter", "boundaryType", (param) =>
        param ? "CLIP_TO_PERIMETER" : "BOUNDING_BOX",
      );

      renameRoofParameter(roof, "eaveType");
      renameRoofParameter(roof, "eaveCutType");
      renameRoofParameter(roof, "eaveReturnDistance");

      transformRoofParameter(roof, "rakeBrackets", "includeRakeBrackets", (param) => param === "ENABLED");

      // for keeping some behavior that was implemented at roofTrims.ts
      if (roof.roofSettings.includeRakeBrackets) {
        renameRoofParameter(roof, "rakeBracketAtPeak");
        renameRoofParameter(roof, "rakeBracketParams");
        renameRoofParameter(roof, "rakeBracketParamsNormalized");
        renameRoofParameter(roof, "rakeBracketTruncationParams");
        renameRoofParameter(roof, "includeRakeBracketsOnFrontOnly");

        roof.roofSettings.rakeBracketParams = roof.roofSettings.rakeBracketParams ?? [];
        roof.roofSettings.rakeBracketParamsNormalized = roof.roofSettings.rakeBracketParamsNormalized ?? false;
        roof.roofSettings.rakeBracketAtPeak = roof.roofSettings.rakeBracketAtPeak ?? false;
        roof.roofSettings.rakeBracketTruncationParams = roof.roofSettings.rakeBracketTruncationParams ?? [];
        roof.roofSettings.includeRakeBracketsOnFrontOnly = roof.roofSettings.includeRakeBracketsOnFrontOnly ?? false;
      }

      renameRoofParameter(roof, "rakeFriezeBoard");
      renameRoofParameter(roof, "eaveFriezeBoard");
      renameRoofParameter(roof, "wrapFriezeBoardMode");

      if (roof.fullReturnParams !== undefined) {
        roof.roofSettings.addRafterTailExtension = roof.fullReturnParams.addRafterTailExtension;
        roof.roofSettings.rafterTailSlope = roof.fullReturnParams.rafterTailSlope;
        roof.roofSettings.rafterTailMaterial = roof.fullReturnParams.rafterTailMaterial;
        roof.roofSettings.rafterTailExtensionEdgeOffset = roof.fullReturnParams.rafterTailExtensionEdgeOffset;
        roof.roofSettings.rafterTailExtension = roof.fullReturnParams.rafterTailExtension;
        roof.roofSettings.rafterTailDepth = roof.fullReturnParams.rafterTailDepth;
        roof.roofSettings.rafterTailClosed = roof.fullReturnParams.rafterTailClosed;
        delete roof.fullReturnParams;
      }

      if (roof.miniHipParams !== undefined) {
        roof.roofSettings.miniHipMaterial = roof.miniHipParams.miniHipMaterial;
        delete roof.miniHipParams;
      }

      if (roof.miniShedParams !== undefined) {
        roof.roofSettings.miniShedMaterial = roof.miniShedParams.miniShedMaterial;
        delete roof.miniShedParams;
      }

      if (roof.exposedRafterParams !== undefined) {
        roof.roofSettings.lookoutRafters = roof.exposedRafterParams.lookoutRafters;
        roof.roofSettings.flyRafters = roof.exposedRafterParams.flyRafters;
        delete roof.exposedRafterParams;
      }

      renameRoofParameter(roof, "baseHeightRef", "eaveFasciaHeightDatum");

      if (roof.heelHeightOffset !== undefined && roof.rightHeelHeightOffset !== undefined) {
        renameRoofParameter(roof, "heelHeightOffset", "eaveLeftFasciaHeightOffsetOverride");
        renameRoofParameter(roof, "rightHeelHeightOffset", "eaveRightFasciaHeightOffsetOverride");
      } else if (roof.rightHeelHeightOffset !== undefined) {
        renameRoofParameter(roof, "rightHeelHeightOffset", "eaveRightFasciaHeightOffsetOverride");
      } else {
        renameRoofParameter(roof, "heelHeightOffset", "eaveFasciaHeightOffset");
      }

      transformRoofParameter(roof, "suppressRidgeVent", "includeRidgeVent", (param) => !param);

      if (roof.gableRoofVent !== undefined) {
        roof.roofSettings.includeTriangleGableVent = roof.gableRoofVent.enabled;
        roof.roofSettings.triangleGableVentBaseWidth = roof.gableRoofVent.baseWidth;
        delete roof.gableRoofVent;
      }

      renameRoofParameter(roof, "roofTopMaterial", "roofingMaterial");
    }

    // dormers
    for (const dormerId in dormers) {
      const dormer = dormers[dormerId];

      transformDormerParameter(dormer, "rakeBrackets", "includeRakeBrackets", (param) => param === "ENABLED");
      renameDormerParameter(dormer, "roofTopMaterial", "roofingMaterial");
      transformDormerParameter(dormer, "suppressRidgeVent", "includeRidgeVent", (param) => !param);

      if (dormer.gableRoofVent !== undefined) {
        dormer.includeTriangleGableVent = dormer.gableRoofVent.enable;
        dormer.triangleGableVentBaseWidth = dormer.gableRoofVent.baseWidth;
        delete dormer.gableRoofVent;
      }
    }

    return json;
  },
};

export default migration_0_0_133;
